import React, { useState, useEffect, useContext } from 'react';
import { useAuth } from '@/AuthContext';
import useCoopMemberRole from '@/hooks/useCoopMemberRole';
import { ToastContext } from '@/components/ToastProvider';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { formatDT } from '@/utils/dates';

export default function DailyCooperativeStoreSales() {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  const { user } = useAuth();
  const { isAdmin, isFinancial, coopRole } = useCoopMemberRole();
  const { notify } = useContext(ToastContext);

  // Check access when needed, not immediately
  const hasAccess = () => isAdmin() || isFinancial() || coopRole === 'staff';

  useEffect(() => {
    if (hasAccess()) {
      loadSummaries();
    }
  }, [user]); // Re-run when user changes

  const loadSummaries = async () => {
    try {
      setLoading(true);
      const aggregateDailySales = httpsCallable(functions, 'aggregateDailySales');
      const result = await aggregateDailySales({ action: 'list_summaries' });
      
      if (result.data.success) {
        setSummaries(result.data.data);
      } else {
        notify('Failed to load summaries', 'error');
      }
    } catch (error) {
              // Error loading summaries
      notify('Error loading summaries', 'error');
    } finally {
      setLoading(false);
    }
  };

  const generateDailySummary = async () => {
    try {
      setGenerating(true);
      const aggregateDailySales = httpsCallable(functions, 'aggregateDailySales');
      const result = await aggregateDailySales({ 
        action: 'generate', 
        date: selectedDate 
      });
      
      if (result.data.success) {
        notify('Daily summary generated successfully', 'success');
        loadSummaries();
      } else {
        notify(result.data.message || 'Failed to generate summary', 'error');
      }
    } catch (error) {
              // Error generating summary
      notify('Error generating summary', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const approveSummary = async (summaryId) => {
    try {
      setProcessing(true);
      const aggregateDailySales = httpsCallable(functions, 'aggregateDailySales');
      const result = await aggregateDailySales({ 
        action: 'approve', 
        summaryId 
      });
      
      if (result.data.success) {
        notify('Summary approved successfully', 'success');
        loadSummaries();
      } else {
        notify(result.data.message || 'Failed to approve summary', 'error');
      }
    } catch (error) {
              // Error approving summary
      notify('Error approving summary', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const postToAccounting = async (summaryId) => {
    try {
      setProcessing(true);
      const aggregateDailySales = httpsCallable(functions, 'aggregateDailySales');
      const result = await aggregateDailySales({ 
        action: 'post_to_accounting', 
        summaryId 
      });
      
      if (result.data.success) {
        notify('Summary posted to accounting successfully', 'success');
        loadSummaries();
      } else {
        notify(result.data.message || 'Failed to post to accounting', 'error');
      }
    } catch (error) {
              // Error posting to accounting
      notify('Error posting to accounting', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const viewSummary = async (summaryId) => {
    try {
      const aggregateDailySales = httpsCallable(functions, 'aggregateDailySales');
      const result = await aggregateDailySales({ 
        action: 'get_summary', 
        summaryId 
      });
      
      if (result.data.success) {
        setSelectedSummary(result.data.data);
      } else {
        notify('Failed to load summary details', 'error');
      }
    } catch (error) {
              // Error loading summary details
      notify('Error loading summary details', 'error');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_approval: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', text: 'Pending Approval' },
      treasurer_approved: { color: 'bg-blue-100 text-blue-800 border-blue-200', text: 'Treasurer Approved' },
      approved: { color: 'bg-green-100 text-green-800 border-green-200', text: 'Approved' },
      posted_to_accounting: { color: 'bg-purple-100 text-purple-800 border-purple-200', text: 'Posted to Accounting' }
    };

    const config = statusConfig[status] || statusConfig.pending_approval;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (!hasAccess()) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm p-6 text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
            <p className="text-gray-600">You don't have permission to access daily cooperative store sales.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Daily Cooperative Store Sales</h1>
          <p className="text-gray-600">Generate and manage daily sales summaries for accounting</p>
        </div>

        {/* Generate Summary Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Generate Daily Summary</h2>
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Date</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={generateDailySummary}
              disabled={generating}
              className="px-6 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50"
            >
              {generating ? 'Generating...' : 'Generate Summary'}
            </button>
          </div>
        </div>

        {/* Summaries List */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Daily Summaries</h2>
          </div>
          
          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading summaries...</p>
            </div>
          ) : summaries.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-600">No daily summaries found. Generate one to get started.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {summaries.map((summary) => (
                <div key={summary.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex-1">
                      {/* Summary Header */}
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        {getStatusBadge(summary.status)}
                        <span className="text-sm font-mono text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {summary.date}
                        </span>
                        <span className="text-sm text-gray-500">
                          {summary.totalOrders} orders
                        </span>
                      </div>
                      
                      {/* Financial Summary */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Total Capital</p>
                          <p className="text-sm text-gray-600">{formatCurrency(summary.totalCapital)}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Total Sales</p>
                          <p className="text-sm text-gray-600">{formatCurrency(summary.totalSales)}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Total Revenue</p>
                          <p className="text-sm text-gray-600">{formatCurrency(summary.totalRevenue)}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Net Revenue</p>
                          <p className="text-sm font-semibold text-green-600">{formatCurrency(summary.netRevenue)}</p>
                        </div>
                      </div>

                      {/* Approval Status */}
                      <div className="text-sm text-gray-600">
                        {summary.reviewedBy && (
                          <p>Reviewed by: {summary.reviewedBy} on {formatDT(summary.reviewedAt)}</p>
                        )}
                        {summary.approvedBy && (
                          <p>Approved by: {summary.approvedBy} on {formatDT(summary.approvedAt)}</p>
                        )}
                        {summary.postedToAccounting && (
                          <p className="text-green-600 font-medium">
                            Posted to accounting on {formatDT(summary.postedAt)}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col space-y-2 lg:ml-4">
                      <button
                        onClick={() => viewSummary(summary.id)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                      >
                        View Details
                      </button>
                      
                      {summary.status === 'pending_approval' && (isFinancial() || isAdmin()) && (
                        <button
                          onClick={() => approveSummary(summary.id)}
                          disabled={processing}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                          {processing ? 'Processing...' : 'Approve'}
                        </button>
                      )}
                      
                      {summary.status === 'treasurer_approved' && isAdmin() && (
                        <button
                          onClick={() => approveSummary(summary.id)}
                          disabled={processing}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                        >
                          {processing ? 'Processing...' : 'Final Approve'}
                        </button>
                      )}
                      
                      {summary.status === 'approved' && (
                        <button
                          onClick={() => postToAccounting(summary.id)}
                          disabled={processing}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                        >
                          {processing ? 'Processing...' : 'Post to Accounting'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Summary Details Modal */}
        {selectedSummary && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900">
                    Daily Summary - {selectedSummary.date}
                  </h2>
                  <button
                    onClick={() => setSelectedSummary(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6">
                {/* Summary Overview */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Summary</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Date:</span>
                        <span className="font-medium">{selectedSummary.date}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Orders:</span>
                        <span className="font-medium">{selectedSummary.totalOrders}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        {getStatusBadge(selectedSummary.status)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Financial Summary</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Capital:</span>
                        <span className="font-medium">{formatCurrency(selectedSummary.totalCapital)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Sales:</span>
                        <span className="font-medium">{formatCurrency(selectedSummary.totalSales)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Revenue:</span>
                        <span className="font-medium">{formatCurrency(selectedSummary.totalRevenue)}</span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span className="text-gray-900 font-semibold">Net Revenue:</span>
                        <span className="text-green-600 font-bold text-lg">{formatCurrency(selectedSummary.netRevenue)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Orders List */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Orders ({selectedSummary.orders?.length || 0})</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Capital</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Selling Price</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedSummary.orders?.map((order, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {order.orderIdDisplay}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {order.customerName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {formatCurrency(order.orderCapital)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {formatCurrency(order.sellingPrice)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                              {formatCurrency(order.revenue)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => setSelectedSummary(null)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
